import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// Helpers
import { v4 } from 'uuid'
import { resolveErrorMessage } from 'helpers'
import { validateFile } from 'helpers/file'

export const STATUS_ERROR = 'error'
export const STATUS_DONE = 'done'
export const STATUS_UPLOADING = 'uploading'

export const ERROR_DUPPLICATE = '! Duplicate file detected'
export const ERROR_TOTAL_SIZE = '! Maximum file size exceeded'

export const useUploadHook = ({
  accept,
  maxFileSize,
  totalSize,
  multiple = false,
  autoupload = false,
  allowDuplicate = true,
  uploadedFiles = [],
  onUpload = () => {},
  onFilesChange = () => {},
}) => {
  const ref = useRef(null)
  const booting = useRef(true)
  const [files, setStateFiles] = useState([])
  const [processing, setProcessing] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(null)

  const setFiles = useCallback(
    (value) => {
      ref.current = onFilesChange
      setStateFiles(value)
    },
    [onFilesChange]
  )

  // Computed
  const count = useMemo(() => {
    let pending = 0
    let failed = 0
    let uploaded = 0
    let uploading = 0

    files.map((x) => {
      if (!x.status) {
        pending += 1
      }

      if (x.status === STATUS_UPLOADING) {
        uploading += 1
      }

      if (x.status === STATUS_ERROR) {
        failed += 1
      }

      if (x.status === STATUS_DONE) {
        uploaded += 1
      }
    })

    return {
      pending,
      uploading,
      failed,
      uploaded,
    }
  }, [files])

  const uploading = useMemo(() => count.uploading > 0, [count.uploading])

  const uploadable = useMemo(() => count.pending > 0, [count.pending])

  // On file change
  const onFileChange = useCallback(
    (fileList) => {
      const list = Array.from(fileList).map((x, index) => {
        // Validate size & ext
        const file = validateFile(x, {
          accept,
          maxFileSize,
        })

        // Check duplicate
        if (allowDuplicate === false) {
          if (files.findIndex((x) => x.name === file.name) !== -1) {
            file.error = ERROR_DUPPLICATE
            file.status = STATUS_ERROR
          }
        }

        // Check total size
        if (totalSize) {
          const currentFileList = Array.from(fileList).slice(0, index + 1)
          const total = [...files, ...currentFileList].reduce(
            (c, x) => c + x.size,
            0
          )

          if (total > totalSize * 1024 * 1024) {
            file.error = ERROR_TOTAL_SIZE
            file.status = STATUS_ERROR
          }
        }

        // Generate id
        file.id = v4()

        // Is new selected file
        file.isNew = true

        return file
      })

      if (list.length) {
        setFiles((pre) => {
          if (multiple) {
            return [...pre, ...list]
          }
          return list
        })
      }
    },
    [accept, allowDuplicate, files, maxFileSize, multiple, setFiles, totalSize]
  )

  // Update file
  const updateFile = useCallback(
    (id, data = {}, replace = false) => {
      setFiles((pre) => {
        const list = [...pre]
        const index = pre.findIndex((file) => file.id === id)

        if (index !== -1) {
          if (replace) {
            // Replace
            list[index] = data
          } else {
            // Update
            for (const key in data) {
              list[index][key] = data[key]
            }
          }
        }

        return list
      })
    },
    [setFiles]
  )

  // Remove file
  const removeFile = useCallback(
    (id) => {
      setFiles((pre) => {
        // Clone
        const list = [...pre]

        // Find
        const index = pre.findIndex((file) => file.id === id)

        if (index !== -1) {
          // Remove
          list.splice(index, 1)

          // Check total size
          if (totalSize) {
            list.map((file, index) => {
              const total = list
                .slice(0, index + 1)
                .reduce((c, x) => c + x.size, 0)

              // Clear current error
              if (
                file.error === ERROR_TOTAL_SIZE &&
                total <= totalSize * 1024 * 1024
              ) {
                const fileValidate = validateFile(file, {
                  accept,
                  maxFileSize,
                })

                if (fileValidate.error) {
                  file.error = fileValidate.error
                  file.status = fileValidate.status
                } else {
                  file.error = null
                  file.status = null
                }
              }
            })
          }
        }

        return list
      })
    },
    [accept, maxFileSize, setFiles, totalSize]
  )

  // On upload file success
  const onSuccess = useCallback(
    (file, data = {}) => {
      // Update file
      updateFile(
        file.id,
        {
          id: file.id,
          name: file.name,
          size: file.size,
          type: file.type,
          isNew: file.isNew,
          ...data,
          status: STATUS_DONE,
        },
        true
      )

      // Clear uploading file
      setUploadingFile(null)
    },
    [updateFile]
  )

  // On update uploading progress
  const onUploadProgress = useCallback(
    (file, { loaded, total }) => {
      // Update uploading percent
      updateFile(file.id, { percent: (loaded / total) * 100 })
    },
    [updateFile]
  )

  // On upload failed
  const onError = useCallback(
    async (file, error) => {
      if (typeof Error === 'string') {
        updateFile(file.id, {
          error,
          status: STATUS_ERROR,
        })
        return
      }

      updateFile(file.id, {
        error: await resolveErrorMessage(error),
        status: STATUS_ERROR,
      })

      // Clear uploading file
      setUploadingFile(null)
    },
    [updateFile]
  )

  // Manual trigger upload
  const upload = () => setProcessing(true)

  // Update init files
  useEffect(() => {
    if (uploadedFiles.length && booting.current) {
      // Prevent update uploaded files on re-render
      booting.current = false

      // Re-format given files
      const formatted = uploadedFiles.map((x) => {
        const file = typeof x === 'string' ? { name: x } : x
        return {
          id: v4(),
          status: STATUS_DONE,
          ...file,
          isNew: false,
        }
      })

      // Set uploaded files
      setStateFiles(formatted)
    }
  }, [uploadedFiles])

  // Watch files change for auto upload
  useEffect(() => {
    // Trigger upload condition
    if ((autoupload || processing) && uploadingFile === null) {
      // Get next upload file
      const file = files.find((x) => !x.status && !x.error)

      if (file) {
        // Set current uploading file
        setUploadingFile(file)

        // Set uploading status
        updateFile(file.id, {
          status: STATUS_UPLOADING,
          percent: file.size < 1000000 ? 10 : 1,
        })

        // Call upload callback
        onUpload({
          file,
          onUploadProgress: (e) => onUploadProgress(file, e),
          onSuccess: (e) => onSuccess(file, e),
          onError: (e) => onError(file, e),
        })
      } else {
        // No file to upload
        setProcessing(false)
      }
    }
  }, [
    autoupload,
    processing,
    files,
    uploadingFile,
    updateFile,
    onUpload,
    onUploadProgress,
    onSuccess,
    onError,
  ])

  // Callback on files change
  useEffect(() => {
    if (ref.current) {
      ref.current(files)
      ref.current = null
    }
  }, [files])

  return {
    files,
    count,
    uploading,
    uploadable,
    upload,
    updateFile,
    removeFile,
    onFileChange,
    setFiles,
  }
}

export default useUploadHook
